<template>
    <span class="fi fh ath-visual-badge" style="flex-shrink: 0;">
        <span class="el-visual-badge"
            :class="[data.uiCname, data.uiHasColorVars ? 'el--color' : '']"
            :style="data.uiColorVars">{{ data.uiLbl }}</span>
    </span>
</template>

<script>
import { isNeString, isNumber } from '@aretheyhappy/utils';

//  cname values with their own el--t-* class rules below
const BADGE_TYPES = [
    'primary', 'secondary', 'warning', 'info', 'active', 'default', 'danger', 'success', 'light', 'dark',
    'positive', 'negative', 'neutral',
];

//  Badges have always drawn 'success' with the active colors
const BADGE_COLOR_OPTIONS = {aliases: {success: 'active'}};

export default {
    name: 'mead-badge',
    props: {
        /**
         * The value or label displayed inside the badge. Numbers are rendered as-is;
         * non-empty strings are passed through I18N translation. Defaults to `false`,
         * which renders as "???".
         */
        lbl: {type: [Boolean, Number, String], default: () => false},
        /**
         * Deprecated: use `color` instead, which takes the same names plus tints and any CSS color.
         *
         * Style/type modifier for the badge (e.g. "primary", "warning", "success",
         * "positive", "negative", "neutral"). Also accepts any COLOR_STYLES value or a tint of one
         * (e.g. "grade-a", "primary-20"). Applied as an `el--t-{cname}` class;
         * any existing "el--t-" prefix on the value is stripped before re-applying it.
         * Defaults to `false`, which applies no type styling.
         * @deprecated Use `color` instead.
         */
        cname: {type: [Boolean, String], default: () => false},
        /**
         * Color of the badge: a COLOR_STYLES value (e.g. "primary"), a tint of one (e.g. "primary-20")
         * or any CSS color (e.g. "#BAC3C4"). Overrides the colors from `cname` when set. As with
         * `cname`, "success" uses the active colors.
         */
        color: {type: String, default: ''},
    },
    computed: {
        data: function () {
            return {
                uiLbl: isNumber(this.lbl)
                    ? this.lbl
                    : isNeString(this.lbl) ? MeadCommon.I18N.go(this.lbl) : '???',
                uiCname: isNeString(this.cname)
                    ? `el--t-${this.cname.replace('el--t-', '')}`
                    : '',
                uiColorVars: this.uiColorVars,
                uiHasColorVars: Object.keys(this.uiColorVars).length > 0,
            };
        },
        //  `color` always draws from --badge-bg/--badge-fg. A cname without its own class rule
        //  (e.g. 'grade-a' or 'primary-20') does too
        uiColorVars: function () {
            const names = {bg: '--badge-bg', fg: '--badge-fg'};
            if (isNeString(this.color)) return MeadCommon.colorVars(this.color, names, BADGE_COLOR_OPTIONS);
            if (!isNeString(this.cname)) return {};

            const type = this.cname.replace('el--t-', '');
            return BADGE_TYPES.includes(type)
                ? {}
                : MeadCommon.colorVars(type, names, {...BADGE_COLOR_OPTIONS, allowCustom: false});
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

.el-visual-badge {
    user-select: none;
    display: inline-block;
    padding: 0 .75rem;
    font-size: $fs-xs;
    font-weight: bold;
    line-height: 2rem;
    height: 2rem;
    letter-spacing: $ls-m;
    vertical-align: middle;
    white-space: nowrap;
    text-align: center;
    text-decoration: none;
    border-radius: $br-m;
    font-family: $ff-b;
    border: none;
    box-shadow: none;
    color: var(--c-brand-default-fg);
    background-color: var(--c-brand-default-bg);

    &:focus,
    &:hover,
    &:active {
        outline: none;
    }

    //  MODIFIERS

    &.el--standalone {
        pointer-events: auto;
        width: 100%;
        font-size: $fs-s;
        line-height: $lh-s;
        height: auto;
        padding: $s-s;
        word-break: break-all;
        white-space: initial;
        font-weight: 400;
    }

    //  TYPES

    @mixin elBadgeType ($name, $bg, $fg) {
        &.el--t-#{$name} {
            background-color: $bg;
            color: $fg;
        }
    }

    @include elBadgeType('primary', var(--c-brand-primary-bg), var(--c-brand-primary-fg));
    @include elBadgeType('secondary', var(--c-brand-secondary-bg), var(--c-brand-secondary-fg));
    @include elBadgeType('warning', var(--c-brand-warning-bg), var(--c-brand-warning-fg));
    @include elBadgeType('info', var(--c-brand-info-bg), var(--c-brand-info-fg));
    @include elBadgeType('active', var(--c-brand-active-bg), var(--c-brand-active-fg));
    @include elBadgeType('default', var(--c-brand-default-bg), var(--c-brand-default-fg));
    @include elBadgeType('danger', var(--c-brand-danger-bg), var(--c-brand-danger-fg));
    @include elBadgeType('success', var(--c-brand-active-bg), var(--c-brand-active-fg));
    @include elBadgeType('light', var(--c-brand-light-bg), var(--c-brand-light-fg));
    @include elBadgeType('dark', var(--c-brand-dark-bg), var(--c-brand-dark-fg));
    @include elBadgeType('positive', var(--c-sentiment-positive-bg), var(--c-sentiment-positive-fg));
    @include elBadgeType('negative', var(--c-sentiment-negative-bg), var(--c-sentiment-negative-fg));
    @include elBadgeType('neutral', var(--c-sentiment-neutral-bg), var(--c-sentiment-neutral-fg));

    // Any other color from the `color` or `cname` prop, set as --badge-bg/--badge-fg
    &.el--color {
        background-color: var(--badge-bg);
        color: var(--badge-fg, var(--c-txt));
    }

    //  SIZES

    @mixin elBadgeSize ($name, $fs) {
        &.el--s-#{$name} {
            font-size: $fs;
            height: calc($fs + .8rem);
            line-height: calc($fs + .8rem);
        }
    }

    @include elBadgeSize('xxs', $fs-xxs);
    @include elBadgeSize('xs', $fs-xs);
    @include elBadgeSize('s', $fs-s);
}
</style>
